import {
  getDisplayableRoasBaseline,
  TOTAL_FALLBACK_STANDARD_MODE,
} from '@/lib/dr-utils'
import { formatMultiplier } from '@/lib/format-utils'
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import {
  CartesianGrid,
  ComposedChart,
  XAxis,
  YAxis,
  ReferenceLine,
  Line,
  Bar,
  Area,
  ReferenceArea,
} from 'recharts'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ChartTooltip } from '@/components/ui/chart'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { Button } from '@/components/ui/button'
import { format } from 'date-fns'
import { getWeek } from '@/lib/date-utils'
import { useRoasStore } from '@/stores/roas'
import { colorRules } from '../../../lib/color-rules'
import { TargetDays } from './base-virtualized-table'
import ScrollableChart from '@/components/zthd/ScrollableChart'
import {
  buildNiceTicks,
  DEFAULT_CHART_MARGIN,
} from '@/components/zthd/chart-axis-utils'
import { shortCampaignLabel } from '../utils'

const CHART_MARGIN = DEFAULT_CHART_MARGIN
const Y_TICK_COUNT = 8

// --- Constants ---

const MODEL_COLORS = [
  '#10b981', // green
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#f97316', // orange
  '#14b8a6', // teal
  '#a855f7', // purple
]

const DAY_COLORS = {
  0: 'hsl(220, 70%, 50%)', // Blue
  3: 'hsl(239, 80.70%, 34.50%)', // Green
  7: 'hsl(142, 76%, 36%)', // Green
  14: 'hsl(38, 92%, 50%)', // Orange
  30: 'hsl(262, 83%, 58%)', // Purple
  60: 'hsl(346, 77%, 49%)', // Red
  90: 'hsl(173, 58%, 39%)', // Teal
  120: 'hsl(43, 74%, 49%)', // Yellow
  150: 'hsl(280, 65%, 60%)', // Magenta
  180: 'hsl(17, 88%, 40%)', // Brown
}

// --- Helpers ---

const getDayColor = (day) => {
  return DAY_COLORS[day] || `hsl(${(day * 137.5) % 360}, 70%, 50%)`
}

const getCompareColor = (day, index = 0) => {
  const mainColor = getDayColor(day)
  // 解析 HSL 值（格式为 'hsl(h, s%, l%)'）
  const hslMatch = mainColor.match(
    /hsl\((\d+),\s*(\d+(?:\.\d+)?)%,\s*(\d+(?:\.\d+)?)%\)/,
  )
  if (!hslMatch) {
    // 如果解析失败，返回基于索引旋转色相的对比颜色
    return `hsl(${((day * 137.5) + (index * 30)) % 360}, 50%, 60%)`
  }
  const h = parseInt(hslMatch[1], 10)
  const s = parseFloat(hslMatch[2])
  const l = parseFloat(hslMatch[3])
  
  // 基于索引调整色相，使不同 campaign 颜色不同
  const newH = (h + index * 40) % 360
  // 降低饱和度，提高亮度，使其与主曲线区分
  const newS = Math.max(30, s - 20)
  const newL = Math.min(85, l + 10)
  return `hsl(${newH}, ${newS}%, ${newL}%)`
}

const hasUsableTargetRoas = (roasLine, totalFallbackMap) => {
  if (!roasLine) return false
  if (typeof roasLine === 'number') return roasLine > 0

  const day0Baselines = roasLine[0]
  if (!day0Baselines || typeof day0Baselines !== 'object') return false

  const dates = Object.keys(day0Baselines)
  if (dates.length === 0) return false

  if (TOTAL_FALLBACK_STANDARD_MODE !== 'hide') return true

  return dates.some((date) => !totalFallbackMap?.[0]?.[date])
}

const getModelColor = (modelKey, availableModels = []) => {
  const modelIndex = availableModels.indexOf(modelKey)
  if (modelIndex === -1) return MODEL_COLORS[0]
  return MODEL_COLORS[modelIndex % MODEL_COLORS.length]
}

// --- Hooks ---

const useChartData = ({
  roas,
  compareRoas,
  selectedDays,
  selectedModels,
  chartMode,
  displayMode,
  availableModels,
  day0RoasLine,
  day0RoasLineTotalFallback,
  /** 'adaptive' 数据范围+10%留白；'fixed' 旧逻辑：含0且20%留白 */
  yAxisMode = 'adaptive',
}) => {
  const {
    dayMode,
    multiplierDenominatorMode,
    setMultiplierDenominatorMode,
    compareDiffMode,
  } = useRoasStore()
  const showPercentage = displayMode === 'percentage'
  const showMultiplier = displayMode === 'multiplier'
  const isDaily = dayMode === 'daily'

  // 检查是否有目标 ROAS 基准线
  const hasTargetRoas = useMemo(() => {
    return hasUsableTargetRoas(day0RoasLine, day0RoasLineTotalFallback)
  }, [day0RoasLine, day0RoasLineTotalFallback])

  // 如果当前模式是目标 ROAS 但数据不存在，自动回退到 D0 Revenue
  useEffect(() => {
    if (multiplierDenominatorMode === 'target_roas' && !hasTargetRoas) {
      setMultiplierDenominatorMode('d0_revenue')
    }
  }, [multiplierDenominatorMode, hasTargetRoas, setMultiplierDenominatorMode])

  const getDenominator = (row, isCompare = false) => {
    if (multiplierDenominatorMode === 'growth_rate') return null

    if (multiplierDenominatorMode === 'target_roas') {
      const target = getDisplayableRoasBaseline(
        day0RoasLine,
        day0RoasLineTotalFallback,
        0,
        row.install_date,
        TOTAL_FALLBACK_STANDARD_MODE,
      )
      return row.cost * target
    }

    // d0_revenue - Lock to REAL Day 0 Revenue
    // row.days contains day0 for both main and compare rows in this context
    return row.days?.['day0'] || 0
  }

  const maxDay = useMemo(() => {
    let highest = 0
    const checkData = (data) => {
      data?.forEach((row) => {
        if (row.days) {
          Object.keys(row.days).forEach((k) => {
            const d = parseInt(k.replace('day', ''), 10)
            if (!Number.isNaN(d) && d > highest) {
              highest = d
            }
          })
        }
      })
    }
    checkData(roas?.dailyData)
    if (Array.isArray(compareRoas)) {
      compareRoas.forEach((c) => checkData(c?.dailyData))
    } else {
      checkData(compareRoas?.dailyData)
    }
    // Ensure we at least allow some basic days if no data
    return Math.max(highest, 30)
  }, [roas?.dailyData, compareRoas])

  const { chartData, values } = useMemo(() => {
    if (!roas?.dailyData) return { chartData: [], values: [] }

    const collectedValues = []
    const data = roas.dailyData
      .map((row) => {
        const installDate = new Date(row.install_date)
        const today = new Date()
        const daysDiff = Math.floor(
          (today - installDate) / (1000 * 60 * 60 * 24),
        )

        // Format date with weekday
        const dateStr = row.install_date.replace(/-/g, '')
        let formattedDate
        if (row.displayDate) {
          formattedDate = row.displayDate
        } else {
          const [, weekDay] = getWeek(dateStr)
          formattedDate = `${format(
            new Date(row.install_date),
            'MM-dd',
          )} 周${weekDay}`
        }

        const dayCosts = row.dayCosts || {}
        const cumulativeRevenueDays = row.cumulativeRevenueDays || {}

        const dataPoint = {
          date: formattedDate,
          install_date: new Date(row.install_date),
          cost: row.cost,
        }

        // Process Selected Days
        selectedDays.forEach((selectedDay) => {
          // Use pre-calculated availability from dayCosts or cumulativeRevenueDays if available (summary mode)
          // Or fallback to days check (daily mode)
          const dayKey = `day${selectedDay}`

          const hasSummaryData = cumulativeRevenueDays[dayKey] !== undefined
          const hasDailyData = row.days && row.days[dayKey] !== undefined

          const hasData = row.displayDate ? hasSummaryData : hasDailyData
          const hasSelectedDayData = !(daysDiff < selectedDay || !hasData)

          // Actual Data
          if (!hasSelectedDayData) {
            dataPoint[`day${selectedDay}`] = null
            dataPoint[`day${selectedDay}_hasData`] = false
            dataPoint[`day${selectedDay}_displayValue`] = '-'
          } else {
            let dataValue = 0

            if (isDaily) {
              // Daily Mode: Use exact day value
              dataValue = row.days?.[`day${selectedDay}`] || 0
            } else {
              // Cumulative Mode
              if (row.displayDate && hasSummaryData) {
                dataValue = cumulativeRevenueDays[dayKey]
              } else {
                for (let i = 0; i <= selectedDay; i++) {
                  dataValue += row.days[`day${i}`] || 0
                }
              }
            }

            // Determine applicable cost
            const applicableCost =
              row.displayDate && dayCosts[dayKey] ? dayCosts[dayKey] : row.cost

            let value = 0
            let displayValue = '-'

            if (showPercentage && applicableCost) {
              value = (dataValue / applicableCost) * 100
              displayValue = `${value.toFixed(2)}%`
            } else if (showMultiplier) {
              if (multiplierDenominatorMode === 'growth_rate') {
                if (selectedDay === 0) {
                  value = 0
                } else {
                  let prevVal = 0
                  if (isDaily) {
                    prevVal = row.days?.[`day${selectedDay - 1}`] || 0
                  } else {
                    if (
                      row.displayDate &&
                      cumulativeRevenueDays[`day${selectedDay - 1}`] !==
                        undefined
                    ) {
                      prevVal = cumulativeRevenueDays[`day${selectedDay - 1}`]
                    } else {
                      for (let i = 0; i < selectedDay; i++) {
                        prevVal += row.days?.[`day${i}`] || 0
                      }
                    }
                  }
                  if (prevVal) {
                    value = dataValue / prevVal
                  }
                }
              } else {
                const denom = getDenominator(row)
                if (denom) value = dataValue / denom
              }
              displayValue = formatMultiplier(value)
            } else {
              value = dataValue
              displayValue = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
              }).format(dataValue)
            }

            // Handle edge cases
            if (showPercentage && !applicableCost) {
              dataPoint[`day${selectedDay}`] = null
              dataPoint[`day${selectedDay}_hasData`] = true
              dataPoint[`day${selectedDay}_displayValue`] = '-'
            } else {
              dataPoint[`day${selectedDay}`] = value || 0
              dataPoint[`day${selectedDay}_hasData`] = true
              dataPoint[`day${selectedDay}_displayValue`] = displayValue
            }

            // 收集所有数值用于 Y 轴自适应（含负值，便于百分比模式正确算区间）
            if (
              chartMode !== 'bar-box' &&
              !compareDiffMode &&
              typeof value === 'number'
            ) {
              collectedValues.push(value)
            }
          }

          // Prediction Data (disabled in compare mode)
          if (
            !compareRoas ||
            (Array.isArray(compareRoas) && compareRoas.length === 0)
          ) {
            const allModelsData = row.allModelsData
            if (
              allModelsData &&
              allModelsData instanceof Map &&
              selectedModels.length > 0
            ) {
              selectedModels.forEach((modelKey) => {
                const modelData = allModelsData.get(modelKey)
                if (!modelData) {
                  setNullPrediction(dataPoint, selectedDay, modelKey)
                  return
                }

                // Check max day for this model
                const modelDayKeys = Object.keys(modelData)
                const modelMaxDay =
                  modelDayKeys.length > 0
                    ? Math.max(
                        ...modelDayKeys
                          .map((key) => parseInt(key.replace('day', ''), 10))
                          .filter((n) => !Number.isNaN(n)),
                      )
                    : -1

                if (selectedDay > modelMaxDay) {
                  setNullPrediction(dataPoint, selectedDay, modelKey)
                  return
                }

                let predictDataValue = 0
                let hasAnyPredictData = false

                if (isDaily) {
                  const dayValue = modelData[`day${selectedDay}`]
                  if (
                    dayValue !== undefined &&
                    dayValue !== null &&
                    dayValue !== ''
                  ) {
                    predictDataValue = Number(dayValue) || 0
                    hasAnyPredictData = true
                  }
                } else {
                  for (let i = 0; i <= selectedDay; i++) {
                    const dayValue = modelData[`day${i}`]
                    if (
                      dayValue !== undefined &&
                      dayValue !== null &&
                      dayValue !== ''
                    ) {
                      predictDataValue += Number(dayValue) || 0
                      hasAnyPredictData = true
                    }
                  }
                }

                if (!hasAnyPredictData) {
                  setNullPrediction(dataPoint, selectedDay, modelKey)
                  return
                }

                if ((showPercentage && !row.cost) || showMultiplier) {
                  let canCalc = false
                  if (showMultiplier) {
                    if (multiplierDenominatorMode === 'growth_rate') {
                      canCalc = true
                    } else {
                      const denom = getDenominator(row)
                      if (denom) canCalc = true
                    }
                  }
                  setNullPrediction(dataPoint, selectedDay, modelKey, !canCalc)
                } else {
                  let predictValue = 0
                  let predictDisplayValue = '-'

                  if (showPercentage && row.cost) {
                    // 优先使用 roasAcc 官方累计 ROAS（不通过 revenue/cost 反推）
                    const roasAccDirect = row.predictRoasAccDays?.[`day${selectedDay}`]
                    if (roasAccDirect != null) {
                      predictValue = Number(roasAccDirect) * 100
                    } else {
                      // 降级：用 predictRevenueAccDays（累计收入）计算，避免误用 revenueDaily（单日收入）
                      const revenueAcc = row.predictRevenueAccDays?.[`day${selectedDay}`]
                      const revForCalc = revenueAcc != null ? Number(revenueAcc) : predictDataValue
                      predictValue = (revForCalc / row.cost) * 100
                    }
                    predictDisplayValue = `${predictValue.toFixed(2)}%`
                  } else if (showMultiplier) {
                    if (multiplierDenominatorMode === 'growth_rate') {
                      if (selectedDay === 0) {
                        predictValue = 0
                      } else {
                        let prevVal = 0
                        if (isDaily) {
                          prevVal = modelData[`day${selectedDay - 1}`] || 0
                        } else {
                          for (let i = 0; i < selectedDay; i++) {
                            prevVal += Number(modelData[`day${i}`]) || 0
                          }
                        }
                        if (prevVal) {
                          predictValue = predictDataValue / prevVal
                        }
                      }
                    } else {
                      const denom = getDenominator(row)
                      if (denom) predictValue = predictDataValue / denom
                    }
                    predictDisplayValue = formatMultiplier(predictValue)
                  } else {
                    predictValue = predictDataValue
                    predictDisplayValue = new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD',
                    }).format(predictDataValue)
                  }

                  dataPoint[`day${selectedDay}_predict_${modelKey}`] =
                    predictValue
                  dataPoint[
                    `day${selectedDay}_predict_${modelKey}_hasData`
                  ] = true
                  dataPoint[
                    `day${selectedDay}_predict_${modelKey}_displayValue`
                  ] = predictDisplayValue

                  if (
                    chartMode !== 'bar-box' &&
                    typeof predictValue === 'number'
                  ) {
                    collectedValues.push(predictValue)
                  }
                }
              })
            } else {
              selectedModels.forEach((modelKey) =>
                setNullPrediction(dataPoint, selectedDay, modelKey),
              )
            }
          }

          if (
            (!compareRoas ||
              (Array.isArray(compareRoas) && compareRoas.length === 0)) &&
            chartMode === 'bar-box' &&
            hasSelectedDayData &&
            dataPoint[`day${selectedDay}`] !== null
          ) {
            const actualValue = dataPoint[`day${selectedDay}`]
            selectedModels.forEach((modelKey) => {
              const predictValue =
                dataPoint[`day${selectedDay}_predict_${modelKey}`]
              const hasPredictData =
                dataPoint[`day${selectedDay}_predict_${modelKey}_hasData`]

              if (hasPredictData && predictValue !== null) {
                const diff = predictValue - actualValue
                dataPoint[`day${selectedDay}_diff_${modelKey}`] = diff
                dataPoint[`day${selectedDay}_diff_${modelKey}_hasData`] = true
                collectedValues.push(diff)
              } else {
                dataPoint[`day${selectedDay}_diff_${modelKey}`] = null
                dataPoint[`day${selectedDay}_diff_${modelKey}_hasData`] = false
              }
            })
          }
        })

        return dataPoint
      })
      .sort((a, b) => a.install_date - b.install_date)

    // Merge compare data if exists
    if (
      compareRoas &&
      (Array.isArray(compareRoas)
        ? compareRoas.length > 0
        : compareRoas.dailyData)
    ) {
      const compareCampaigns = Array.isArray(compareRoas)
        ? compareRoas
        : [compareRoas]
      const mergedData = [...data]
      // 每个 compare：非日期对比为 Map(install_date -> point)，日期对比为按 install_date 排序的数组，与主表按索引对齐
      const buildCompareDataPoint = (row, idx) => {
        const installDate = new Date(row.install_date)
        const today = new Date()
        const daysDiff = Math.floor(
          (today - installDate) / (1000 * 60 * 60 * 24),
        )
        const compareDateStr = row.install_date.replace(/-/g, '')
        let compareFormattedDate
        if (row.displayDate) {
          compareFormattedDate = row.displayDate
        } else {
          const [, compareWeekDay] = getWeek(compareDateStr)
          compareFormattedDate = `${format(
            new Date(row.install_date),
            'MM-dd',
          )} 周${compareWeekDay}`
        }
        const compareDayCosts = row.dayCosts || {}
        const compareCumulativeRevenueDays = row.cumulativeRevenueDays || {}
        const compareDataPoint = {
          date: compareFormattedDate,
          install_date: new Date(row.install_date),
          cost: row.cost,
        }
        selectedDays.forEach((selectedDay) => {
          const dayKey = `day${selectedDay}`
          const hasSummaryData =
            compareCumulativeRevenueDays[dayKey] !== undefined
          const hasDailyData = row.days && row.days[dayKey] !== undefined
          const hasData = row.displayDate ? hasSummaryData : hasDailyData

          if (daysDiff < selectedDay || !hasData) {
            compareDataPoint[`day${selectedDay}_compare_${idx}`] = null
            compareDataPoint[`day${selectedDay}_compare_${idx}_hasData`] = false
            compareDataPoint[`day${selectedDay}_compare_${idx}_displayValue`] = '-'
          } else {
            let dataValue = 0
            if (isDaily) {
              dataValue = row.days?.[`day${selectedDay}`] || 0
            } else {
              if (row.displayDate && hasSummaryData) {
                dataValue = compareCumulativeRevenueDays[dayKey]
              } else {
                for (let i = 0; i <= selectedDay; i++) {
                  dataValue += row.days?.[`day${i}`] || 0
                }
              }
            }
            const applicableCost =
              row.displayDate && compareDayCosts[dayKey]
                ? compareDayCosts[dayKey]
                : row.cost
            let value = 0
            let displayValue = '-'
            if (showPercentage && applicableCost) {
              value = (dataValue / applicableCost) * 100
              displayValue = `${value.toFixed(2)}%`
            } else if (showMultiplier) {
              if (multiplierDenominatorMode === 'growth_rate') {
                if (selectedDay === 0) {
                  value = 0
                } else {
                  let prevVal = 0
                  if (isDaily) {
                    prevVal = row.days?.[`day${selectedDay - 1}`] || 0
                  } else {
                    if (
                      row.displayDate &&
                      compareCumulativeRevenueDays[`day${selectedDay - 1}`] !== undefined
                    ) {
                      prevVal = compareCumulativeRevenueDays[`day${selectedDay - 1}`]
                    } else {
                      for (let i = 0; i < selectedDay; i++) {
                        prevVal += row.days?.[`day${i}`] || 0
                      }
                    }
                  }
                  if (prevVal) value = dataValue / prevVal
                }
              } else {
                const denom = getDenominator(row, true)
                if (denom) value = dataValue / denom
              }
              displayValue = formatMultiplier(value)
            } else {
              value = dataValue
              displayValue = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
              }).format(dataValue)
            }
            if (
              (showPercentage && !applicableCost) ||
              (showMultiplier &&
                multiplierDenominatorMode !== 'growth_rate' &&
                !getDenominator(row, true))
            ) {
              compareDataPoint[`day${selectedDay}_compare_${idx}`] = null
              compareDataPoint[`day${selectedDay}_compare_${idx}_hasData`] = true
              compareDataPoint[`day${selectedDay}_compare_${idx}_displayValue`] = '-'
            } else {
              compareDataPoint[`day${selectedDay}_compare_${idx}`] = value || 0
              compareDataPoint[`day${selectedDay}_compare_${idx}_hasData`] = true
              compareDataPoint[`day${selectedDay}_compare_${idx}_displayValue`] = displayValue
            }
          }
        })
        return compareDataPoint
      }

      const compareCampaignDataMaps = compareCampaigns.map((c, idx) => {
        if (c.isDateCompare && c.dailyData?.length) {
          const sorted = [...c.dailyData].sort(
            (a, b) => new Date(a.install_date) - new Date(b.install_date),
          )
          return sorted.map((row) => buildCompareDataPoint(row, idx))
        }
        const map = new Map()
        c?.dailyData?.forEach((row) => {
          map.set(row.install_date, buildCompareDataPoint(row, idx))
        })
        return map
      })

      // Merge all compare campaigns into main data（日期对比按索引对齐，非日期对比按 install_date 匹配）
      mergedData.forEach((mainPoint, mainIndex) => {
        const mainDateStr = format(mainPoint.install_date, 'yyyy-MM-dd')
        compareCampaignDataMaps.forEach((mapOrArray, idx) => {
          const comparePoint = compareCampaigns[idx].isDateCompare
            ? (Array.isArray(mapOrArray) ? mapOrArray[mainIndex] ?? null : null)
            : mapOrArray.get(mainDateStr)
          if (comparePoint && compareCampaigns[idx].isDateCompare) {
            mainPoint[`compareInstallDate_${idx}`] = comparePoint.install_date
          }
          selectedDays.forEach((selectedDay) => {
            if (comparePoint) {
              const mainValue = mainPoint[`day${selectedDay}`]
              const compareValue =
                comparePoint[`day${selectedDay}_compare_${idx}`]
              const mainHasData = mainPoint[`day${selectedDay}_hasData`]
              const compareHasData =
                comparePoint[`day${selectedDay}_compare_${idx}_hasData`]

              if (
                compareDiffMode &&
                mainHasData &&
                compareHasData &&
                mainValue !== null &&
                compareValue !== null
              ) {
                const diffValue = mainValue - compareValue
                mainPoint[`day${selectedDay}_compare_${idx}`] = diffValue
                mainPoint[`day${selectedDay}_compare_${idx}_hasData`] = true

                // Format display value for diff
                let displayValue = '-'
                if (showPercentage) {
                  displayValue = `${
                    diffValue > 0 ? '+' : ''
                  }${diffValue.toFixed(2)}%`
                } else if (showMultiplier) {
                  displayValue = `${diffValue > 0 ? '+' : ''}${formatMultiplier(
                    diffValue,
                  )}`
                } else {
                  displayValue = new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    signDisplay: 'always',
                  }).format(diffValue)
                }
                mainPoint[`day${selectedDay}_compare_${idx}_displayValue`] =
                  displayValue
                mainPoint[`day${selectedDay}_compare_${idx}_rawDiff`] =
                  diffValue
                if (diffValue !== 0) collectedValues.push(diffValue)
              } else {
                mainPoint[`day${selectedDay}_compare_${idx}`] = compareValue
                mainPoint[`day${selectedDay}_compare_${idx}_hasData`] =
                  compareHasData
                mainPoint[`day${selectedDay}_compare_${idx}_displayValue`] =
                  comparePoint[`day${selectedDay}_compare_${idx}_displayValue`]
                if (typeof compareValue === 'number')
                  collectedValues.push(compareValue)
              }
            } else {
              mainPoint[`day${selectedDay}_compare_${idx}`] = null
              mainPoint[`day${selectedDay}_compare_${idx}_hasData`] = false
              mainPoint[`day${selectedDay}_compare_${idx}_displayValue`] = '-'
            }
          })
          if (comparePoint && !compareCampaigns[idx].isDateCompare) {
            mapOrArray.delete(mainDateStr)
          }
        })
      })

      // Add compare-only dates（仅非日期对比：主表没有的日期单独成点；日期对比已按索引合并，无剩余）
      compareCampaignDataMaps.forEach((mapOrArray, idx) => {
        if (compareCampaigns[idx].isDateCompare || !(mapOrArray instanceof Map)) return
        mapOrArray.forEach((comparePoint, dateStr) => {
          // Check if we already added this date from another compare campaign
          let existingPoint = mergedData.find(
            (p) => format(p.install_date, 'yyyy-MM-dd') === dateStr,
          )
          if (!existingPoint) {
            existingPoint = {
              date: comparePoint.date,
              install_date: comparePoint.install_date,
              cost: comparePoint.cost,
            }
            // Fill main and other compares with null
            selectedDays.forEach((selectedDay) => {
              existingPoint[`day${selectedDay}`] = null
              existingPoint[`day${selectedDay}_hasData`] = false
              existingPoint[`day${selectedDay}_displayValue`] = '-'
              compareCampaigns.forEach((_, cIdx) => {
                existingPoint[`day${selectedDay}_compare_${cIdx}`] = null
                existingPoint[
                  `day${selectedDay}_compare_${cIdx}_hasData`
                ] = false
                existingPoint[
                  `day${selectedDay}_compare_${cIdx}_displayValue`
                ] = '-'
              })
            })
            mergedData.push(existingPoint)
          }
          // Update the specific compare campaign data
          selectedDays.forEach((selectedDay) => {
            const compareValue =
              comparePoint[`day${selectedDay}_compare_${idx}`]
            const compareHasData =
              comparePoint[`day${selectedDay}_compare_${idx}_hasData`]

            // In compare-only dates, mainValue is effectively 0 or null.
            // If compareDiffMode is on, we show the full compare value as the diff (since main is 0)
            // but usually compare-only dates might be confusing in diff mode.
            // Following the logic: diff = compare - main. If main is null, we can treat it as 0 or keep it as is.
            // Let's treat main as 0 if compareDiffMode is on and main data is missing for that date.

            if (compareDiffMode && compareHasData && compareValue !== null) {
              const diffValue = -compareValue // main is null/0
              existingPoint[`day${selectedDay}_compare_${idx}`] = diffValue
              existingPoint[`day${selectedDay}_compare_${idx}_hasData`] = true

              let displayValue = '-'
              if (showPercentage) {
                displayValue = `${diffValue > 0 ? '+' : ''}${diffValue.toFixed(
                  2,
                )}%`
              } else if (showMultiplier) {
                displayValue = `${diffValue > 0 ? '+' : ''}${formatMultiplier(
                  diffValue,
                )}`
              } else {
                displayValue = new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  signDisplay: 'always',
                }).format(diffValue)
              }
              existingPoint[`day${selectedDay}_compare_${idx}_displayValue`] =
                displayValue
              existingPoint[`day${selectedDay}_compare_${idx}_rawDiff`] =
                diffValue
              if (diffValue !== 0) collectedValues.push(diffValue)
            } else {
              existingPoint[`day${selectedDay}_compare_${idx}`] = compareValue
              existingPoint[`day${selectedDay}_compare_${idx}_hasData`] =
                compareHasData
              existingPoint[`day${selectedDay}_compare_${idx}_displayValue`] =
                comparePoint[`day${selectedDay}_compare_${idx}_displayValue`]
              if (typeof compareValue === 'number')
                collectedValues.push(compareValue)
            }
          })
        })
      })

      // 日期对比时在 X 轴标签中显示对比的单个日期（主日期 | 对比 08-29）
      mergedData.forEach((point) => {
        const parts = [point.date]
        compareCampaigns.forEach((c, idx) => {
          if (c.isDateCompare && point[`compareInstallDate_${idx}`]) {
            parts.push('对比 ' + format(point[`compareInstallDate_${idx}`], 'MM-dd'))
          }
        })
        if (parts.length > 1) point.date = parts.join(' | ')
      })

      return {
        chartData: mergedData.sort((a, b) => a.install_date - b.install_date),
        values: collectedValues,
      }
    }

    return { chartData: data, values: collectedValues }
  }, [
    roas?.dailyData,
    compareRoas,
    selectedDays,
    selectedModels,
    chartMode,
    showPercentage,
    showMultiplier,
    isDaily,
    multiplierDenominatorMode,
    day0RoasLine,
    day0RoasLineTotalFallback,
    compareDiffMode,
  ])

  // Y 轴：adaptive=数据范围+10%留白；fixed=旧逻辑（含0且20%留白）
  const { yMin, yMax } = useMemo(() => {
    const dataMin = values.length > 0 ? Math.min(...values) : 0
    const dataMax = values.length > 0 ? Math.max(...values) : 100
    let minValue = dataMin
    let maxValue = dataMax
    if (yAxisMode === 'fixed') {
      minValue = values.length > 0 ? Math.min(0, ...values) : 0
      maxValue = values.length > 0 ? Math.max(0, ...values) : 100
    }
    const valueRange = Math.max(maxValue - minValue, 1)
    const padding = valueRange * (yAxisMode === 'fixed' ? 0.2 : 0.1)
    const yMaxRaw = maxValue + padding
    const yMinRaw = minValue - padding

    if (showPercentage) {
      return {
        yMax: Math.ceil(yMaxRaw / 10) * 10,
        yMin: Math.floor(yMinRaw / 10) * 10,
      }
    }

    if (showMultiplier) {
      return {
        yMax: Math.ceil(yMaxRaw * 10) / 10,
        yMin: Math.floor(yMinRaw * 10) / 10,
      }
    }

    return {
      yMax: Math.ceil(yMaxRaw / 100) * 100,
      yMin: Math.floor(yMinRaw / 100) * 100,
    }
  }, [values, showPercentage, showMultiplier, yAxisMode])

  // Chart Config
  const chartConfig = useMemo(() => {
    const config = {}
    selectedDays.forEach((day) => {
      config[`day${day}`] = {
        label: `Day-${day}`,
        color: getDayColor(day),
      }
      // Add compare data config
      if (compareRoas) {
        const compareCampaigns = Array.isArray(compareRoas)
          ? compareRoas
          : [compareRoas]
        compareCampaigns.forEach((c, idx) => {
          config[`day${day}_compare_${idx}`] = {
            label: `Day-${day} (${shortCampaignLabel(c?.campaignName || 'Compare')})`,
            color: getCompareColor(day, idx),
          }
        })
      }
      // Prediction data config (disabled in compare mode)
      if (
        !compareRoas ||
        (Array.isArray(compareRoas) && compareRoas.length === 0)
      ) {
        selectedModels.forEach((modelKey) => {
          config[`day${day}_predict_${modelKey}`] = {
            label: `Day-${day} (${modelKey})`,
            color: getModelColor(modelKey, availableModels),
          }
          if (chartMode === 'bar-box') {
            config[`day${day}_diff_${modelKey}`] = {
              label: `Day-${day} (${modelKey} Diff)`,
              color: getModelColor(modelKey, availableModels),
            }
          }
        })
      }
    })
    return config
  }, [selectedDays, selectedModels, chartMode, availableModels, compareRoas])

  // Reference Lines
  const referenceLinesData = useMemo(() => {
    if (chartMode !== 'bar-box' || !showPercentage) return []
    return [
      { y: 2, value: '+2%', fill: '#22c55e' },
      { y: -2, value: '-2%', fill: '#ef4444' },
      { y: 5, value: '+5%', fill: '#22c55e' },
      { y: -5, value: '-5%', fill: '#ef4444' },
      { y: 10, value: '+10%', fill: '#22c55e' },
      { y: -10, value: '-10%', fill: '#ef4444' },
      { y: 0, value: '0%', fill: '#94a3b8' },
      ...selectedDays
        .map((day) => {
          const dayRule =
            colorRules.find((r) => r.day === `day${day}`) ||
            colorRules.find((r) => r.day === 'default')
          return [
            {
              y: dayRule.threshold,
              value: `${dayRule.threshold}%`,
              fill: '#22c55e',
            },
            {
              y: dayRule.lowThreshold,
              value: `${dayRule.lowThreshold}%`,
              fill: '#ef4444',
            },
          ]
        })
        .flat(),
    ]
  }, [chartMode, showPercentage, selectedDays])

  return { chartData, yMin, yMax, chartConfig, referenceLinesData, maxDay }
}

// Helper for useChartData
function setNullPrediction(dataPoint, day, modelKey, hasData = false) {
  dataPoint[`day${day}_predict_${modelKey}`] = null
  dataPoint[`day${day}_predict_${modelKey}_hasData`] = hasData
  dataPoint[`day${day}_predict_${modelKey}_displayValue`] = '-'
}

// --- Sub-Components & Helpers ---

const CustomTooltip = ({
  active,
  payload,
  selectedDays,
  selectedModels,
  chartMode,
  showPercentage,
  availableModels,
  compareRoas,
  compareDiffMode,
}) => {
  if (!active || !payload?.length) return null
  const data = payload[0].payload

  return (
    <div className="rounded-lg border bg-background p-2 shadow-sm">
      <div className="flex flex-col gap-1">
        <span className="text-[0.70rem] uppercase text-muted-foreground">
          {data.date}
        </span>
        {selectedDays.map((day) => {
          const value = data[`day${day}_displayValue`]
          const compareCampaigns = Array.isArray(compareRoas)
            ? compareRoas
            : compareRoas
            ? [compareRoas]
            : []
          return (
            <React.Fragment key={day}>
              {value && value !== '-' && (
                <div className="flex items-center gap-2">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: getDayColor(day) }}
                  />
                  <span className="text-xs">Day-{day}:</span>
                  <span className="font-bold text-xs">{value}</span>
                </div>
              )}
              {/* Compare data */}
              {compareCampaigns.map((c, idx) => {
                const compareValue = data[`day${day}_compare_${idx}`]
                if (!compareValue || compareValue === '-') return null
                const shortName = shortCampaignLabel(c?.campaignName || 'Compare')
                const compareDateLabel = c?.isDateCompare && data[`compareInstallDate_${idx}`]
                  ? `${shortName} ${format(data[`compareInstallDate_${idx}`], 'yyyy-MM-dd')}`
                  : shortName
                return (
                  <div
                    key={`${day}_compare_${idx}`}
                    className="flex items-center gap-2"
                  >
                    <div
                      className="w-2 h-2 rounded-full border-2 border-dashed"
                      style={{
                        backgroundColor: 'transparent',
                        borderColor: getCompareColor(day, idx),
                      }}
                    />
                    <span className="text-xs">
                      Day-{day} ({compareDateLabel}
                      {compareDiffMode ? ' Diff' : ''}):
                    </span>
                    <span
                      className={`font-bold text-xs ${
                        compareDiffMode
                          ? data[`day${day}_compare_${idx}_rawDiff`] > 0
                            ? 'text-green-600'
                            : data[`day${day}_compare_${idx}_rawDiff`] < 0
                            ? 'text-red-600'
                            : ''
                          : ''
                      }`}
                    >
                      {data[`day${day}_compare_${idx}_displayValue`]}
                    </span>
                  </div>
                )
              })}
              {/* Prediction data (disabled in compare mode) */}
              {compareCampaigns.length === 0 &&
                selectedModels.map((modelKey) => {
                  const modelColor = getModelColor(modelKey, availableModels)

                  if (chartMode === 'bar-box') {
                    const diffValue = data[`day${day}_diff_${modelKey}`]
                    const hasData = data[`day${day}_diff_${modelKey}_hasData`]

                    if (hasData && diffValue !== undefined) {
                      const formattedDiff = showPercentage
                        ? `${diffValue > 0 ? '+' : ''}${diffValue.toFixed(2)}%`
                        : new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            signDisplay: 'always',
                          }).format(diffValue)

                      return (
                        <div
                          key={`${day}_${modelKey}`}
                          className="flex items-center gap-2"
                        >
                          <div
                            className="w-2 h-2 rounded-full border-2 border-dashed"
                            style={{
                              backgroundColor: 'transparent',
                              borderColor: modelColor,
                            }}
                          />
                          <span className="text-xs">
                            Day-{day} ({modelKey}):
                          </span>
                          <span
                            className={`font-bold text-xs ${
                              diffValue > 0
                                ? 'text-green-600'
                                : diffValue < 0
                                ? 'text-red-600'
                                : ''
                            }`}
                          >
                            {formattedDiff}
                          </span>
                        </div>
                      )
                    }
                  } else {
                    const predictValue =
                      data[`day${day}_predict_${modelKey}_displayValue`]
                    if (predictValue && predictValue !== '-') {
                      return (
                        <div
                          key={`${day}_${modelKey}`}
                          className="flex items-center gap-2"
                        >
                          <div
                            className="w-2 h-2 rounded-full border-2 border-dashed"
                            style={{
                              backgroundColor: 'transparent',
                              borderColor: modelColor,
                            }}
                          />
                          <span className="text-xs">
                            Day-{day} ({modelKey}):
                          </span>
                          <span className="font-bold text-xs">
                            {predictValue}
                          </span>
                        </div>
                      )
                    }
                  }
                  return null
                })}
            </React.Fragment>
          )
        })}
      </div>
    </div>
  )
}

// Helper function to render chart visuals (Bars, Lines, etc.)
// NOTE: This must be a function called directly in JSX, not a component,
// because Recharts needs to see the actual Bar/Line components as children.
const renderChartVisuals = ({
  chartMode,
  selectedDays,
  selectedModels,
  chartData,
  showPercentage,
  availableModels,
  compareRoas,
  compareDiffMode,
  areaBaseValue = 0,
}) => {
  return (
    <React.Fragment>
      <defs>
        {selectedDays.map((day) => (
          <linearGradient
            key={day}
            id={`colorDay${day}`}
            x1="0"
            y1="0"
            x2="0"
            y2="1"
          >
            <stop offset="5%" stopColor={getDayColor(day)} stopOpacity={0.5} />
            <stop offset="95%" stopColor={getDayColor(day)} stopOpacity={0.1} />
          </linearGradient>
        ))}
        {/* Compare data gradients */}
        {compareRoas &&
          selectedDays.map((day) => {
            const compareCampaigns = Array.isArray(compareRoas)
              ? compareRoas
              : [compareRoas]
            return compareCampaigns.map((_, idx) => (
              <linearGradient
                key={`compareDay${day}_${idx}`}
                id={`colorDay${day}_compare_${idx}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="5%"
                  stopColor={getCompareColor(day, idx)}
                  stopOpacity={0.3}
                />
                <stop
                  offset="95%"
                  stopColor={getCompareColor(day, idx)}
                  stopOpacity={0.1}
                />
              </linearGradient>
            ))
          })}
        {(chartMode === 'bar' || chartMode === 'bar-box') &&
          !compareRoas &&
          selectedDays.map((day) =>
            selectedModels.map((modelKey) => {
              const modelColor = getModelColor(modelKey, availableModels)
              const dayColor = getDayColor(day)
              return (
                <pattern
                  key={`hatch-${day}-${modelKey}`}
                  id={`hatch-${day}-${modelKey}`}
                  patternUnits="userSpaceOnUse"
                  width="8"
                  height="8"
                >
                  <rect
                    width="8"
                    height="8"
                    fill={dayColor}
                    fillOpacity={0.2}
                  />
                  <path
                    d="M0,8 L8,0"
                    stroke={modelColor}
                    strokeWidth="1.5"
                    opacity={0.8}
                  />
                </pattern>
              )
            }),
          )}
      </defs>

      {chartMode === 'bar-box' && !compareRoas && showPercentage && (
        <React.Fragment>
          <ReferenceArea
            y1={-5}
            y2={5}
            fill="#22c55e"
            fillOpacity={0.1}
            stroke="none"
            label={{
              value: 'Safe Zone',
              position: 'insideTopRight',
              fill: '#22c55e',
              fontSize: 12,
              opacity: 0.5,
            }}
          />
          <ReferenceArea
            y1={-2}
            y2={2}
            fill="#3b82f6"
            fillOpacity={0.15}
            stroke="none"
            label={{
              value: 'Excellent',
              position: 'insideRight',
              fill: '#3b82f6',
              fontSize: 12,
              fontWeight: 'bold',
              opacity: 0.8,
            }}
          />
          {[2, -2, 5, -5, 10, -10].map((val) => (
            <ReferenceLine
              key={val}
              y={val}
              stroke={val > 0 ? '#22c55e' : '#ef4444'}
              strokeDasharray="3 3"
              strokeOpacity={0.6}
            />
          ))}
          <ReferenceLine
            y={0}
            stroke="#94a3b8"
            strokeDasharray="3 3"
            strokeOpacity={0.6}
          />
        </React.Fragment>
      )}

      {compareDiffMode && (
        <ReferenceLine
          y={0}
          stroke="#94a3b8"
          strokeWidth={1}
          strokeOpacity={0.8}
        />
      )}

      {/* Percentage Threshold Lines */}
      {showPercentage &&
        selectedDays.map((day) => {
          const dayRule =
            colorRules.find((r) => r.day === `day${day}`) ||
            colorRules.find((r) => r.day === 'default')
          return (
            <React.Fragment key={`ref-${day}`}>
              <ReferenceLine
                y={dayRule.threshold}
                stroke="#22c55e"
                strokeDasharray="3 3"
                strokeOpacity={0.6}
              />
              <ReferenceLine
                y={dayRule.lowThreshold}
                stroke="#ef4444"
                strokeDasharray="3 3"
                strokeOpacity={0.6}
              />
            </React.Fragment>
          )
        })}

      {/* Bars and Lines */}
      {chartMode === 'bar'
        ? selectedDays.map((day) => (
            <React.Fragment key={day}>
              {!compareDiffMode && (
                <Bar
                  dataKey={`day${day}`}
                  fill={getDayColor(day)}
                  radius={[4, 4, 0, 0]}
                />
              )}
              {/* Compare data bar */}
              {compareRoas && (
                <React.Fragment>
                  {(Array.isArray(compareRoas)
                    ? compareRoas
                    : [compareRoas]
                  ).map((_, idx) => (
                    <Bar
                      key={`day${day}_compare_${idx}`}
                      dataKey={`day${day}_compare_${idx}`}
                      fill={getCompareColor(day, idx)}
                      radius={[4, 4, 0, 0]}
                      stroke={getCompareColor(day, idx)}
                      strokeWidth={1.5}
                      strokeDasharray="5 5"
                      fillOpacity={0.6}
                    />
                  ))}
                </React.Fragment>
              )}
              {/* Prediction data bars (disabled in compare mode) */}
              {!compareRoas &&
                selectedModels
                  .filter((modelKey) => {
                    const dataKey = `day${day}_predict_${modelKey}`
                    return chartData.some(
                      (point) =>
                        point[dataKey] !== null &&
                        point[dataKey] !== undefined &&
                        point[`day${day}_predict_${modelKey}_hasData`],
                    )
                  })
                  .map((modelKey) => (
                    <Bar
                      key={`${day}_predict_${modelKey}`}
                      dataKey={`day${day}_predict_${modelKey}`}
                      fill={`url(#hatch-${day}-${modelKey})`}
                      stroke={getModelColor(modelKey, availableModels)}
                      strokeWidth={1.5}
                      strokeDasharray="3 3"
                      radius={[4, 4, 0, 0]}
                    />
                  ))}
            </React.Fragment>
          ))
        : chartMode === 'bar-box'
        ? !compareRoas &&
          selectedDays.map((day) =>
            selectedModels.map((modelKey) => (
              <Bar
                key={`day-${day}-${modelKey}-diff-bar`}
                dataKey={`day${day}_diff_${modelKey}`}
                fill={getModelColor(modelKey, availableModels)}
                radius={[2, 2, 0, 0]}
              />
            )),
          )
        : selectedDays.map((day) => (
            <React.Fragment key={day}>
              {!compareDiffMode && (
                <Area
                  type="monotone"
                  dataKey={`day${day}`}
                  baseValue={areaBaseValue}
                  stroke={getDayColor(day)}
                  fill={`url(#colorDay${day})`}
                  strokeWidth={2}
                  connectNulls={false}
                  dot={{ fill: getDayColor(day), r: 3 }}
                  activeDot={{ r: 5, fill: getDayColor(day) }}
                />
              )}
              {/* Compare data line */}
              {compareRoas && (
                <React.Fragment>
                  {(Array.isArray(compareRoas)
                    ? compareRoas
                    : [compareRoas]
                  ).map((_, idx) => (
                    <Line
                      key={`day${day}_compare_${idx}`}
                      type="monotone"
                      dataKey={`day${day}_compare_${idx}`}
                      stroke={getCompareColor(day, idx)}
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={{ fill: getCompareColor(day, idx), r: 3 }}
                      activeDot={{ r: 5, fill: getCompareColor(day, idx) }}
                      connectNulls={false}
                    />
                  ))}
                </React.Fragment>
              )}
            </React.Fragment>
          ))}

      {/* Prediction Lines (for non-bar-box modes, disabled in compare mode) */}
      {!compareRoas &&
        chartMode !== 'bar-box' &&
        selectedDays.map((day) =>
          selectedModels.map((modelKey) => {
            const dataKey = `day${day}_predict_${modelKey}`
            const hasData = chartData.some(
              (point) =>
                point[dataKey] !== null &&
                point[dataKey] !== undefined &&
                point[`day${day}_predict_${modelKey}_hasData`],
            )
            if (!hasData) return null
            return (
              <Line
                key={`${day}_predict_${modelKey}`}
                type="monotone"
                dataKey={dataKey}
                name={`Day-${day} (${modelKey})`}
                stroke={getModelColor(modelKey, availableModels)}
                strokeWidth={0}
                dot={{
                  r: 5,
                  fill: getModelColor(modelKey, availableModels),
                  stroke: '#fff',
                  strokeWidth: 2,
                }}
                activeDot={{ r: 7 }}
                connectNulls={false}
                isAnimationActive={false}
              />
            )
          }),
        )}
    </React.Fragment>
  )
}

// --- Main Component ---

const Day0Chart = ({
  roas,
  compareRoas,
  dayIndex = 0,
  day0RoasLine,
  day0RoasLineTotalFallback,
}) => {
  const {
    displayMode,
    setDisplayMode,
    dayMode,
    setDayMode,
    multiplierDenominatorMode,
    setMultiplierDenominatorMode,
    compareDiffMode,
    setCompareDiffMode,
  } = useRoasStore()
  const [selectedDays, setSelectedDays] = useState([0])
  const [chartMode, setChartMode] = useState('line')
  const scrollAreaRef = useRef(null)
  /** Y 轴：adaptive=数据范围+10%留白（默认）；fixed=旧逻辑含0且20%留白 */
  const [yAxisMode, setYAxisMode] = useState('adaptive')
  /** X 轴宽度：fit=一屏内；scrollable=可横向滚动看细节。默认 Fit */
  const [chartWidthMode, setChartWidthMode] = useState('fit')

  // 检查是否有目标 ROAS 基准线
  const hasTargetRoas = useMemo(() => {
    return hasUsableTargetRoas(day0RoasLine, day0RoasLineTotalFallback)
  }, [day0RoasLine, day0RoasLineTotalFallback])

  const availableModels = useMemo(() => {
    if (!roas?.dailyData) return []
    const modelsSet = new Set()
    roas.dailyData.forEach((row) => {
      const allModelsData = row.allModelsData
      if (allModelsData && allModelsData instanceof Map) {
        allModelsData.forEach((_, modelKey) => modelsSet.add(modelKey))
      }
    })
    return Array.from(modelsSet)
  }, [roas?.dailyData])

  // 冠军模式下自动全选所有可用模型（通常只有一个冠军模型）
  const selectedModels = useMemo(() => {
    if (compareRoas) return []
    return availableModels
  }, [availableModels, compareRoas])

  const scrollToDay = useCallback((day) => {
    if (scrollAreaRef.current) {
      const scrollArea = scrollAreaRef.current
      let viewport =
        scrollArea.querySelector('[data-radix-scroll-area-viewport]') ||
        scrollArea.querySelector('div[style*="overflow"]') ||
        scrollArea.firstElementChild

      if (viewport) {
        let targetTab =
          viewport.querySelector(`button[value="${day}"]`) ||
          viewport.querySelector(`button[value="${day.toString()}"]`)

        if (!targetTab) {
          const allTabs = viewport.querySelectorAll('button')
          for (const tab of allTabs) {
            if (
              tab.textContent?.includes(`Day${day}`) ||
              tab.getAttribute('value') === day.toString()
            ) {
              targetTab = tab
              break
            }
          }
        }

        if (targetTab) {
          const tabOffsetLeft = targetTab.offsetLeft
          const tabWidth = targetTab.offsetWidth
          const viewportWidth = viewport.offsetWidth || viewport.clientWidth
          const scrollLeft = tabOffsetLeft - viewportWidth / 2 + tabWidth / 2

          if (viewport.scrollTo) {
            viewport.scrollTo({
              left: Math.max(0, scrollLeft),
              behavior: 'smooth',
            })
          } else {
            viewport.scrollLeft = Math.max(0, scrollLeft)
          }
        }
      }
    }
  }, [])

  const handleDaySelect = (day) => {
    setSelectedDays((prev) => {
      if (prev.includes(day)) {
        return prev.length > 1 ? prev.filter((d) => d !== day) : prev
      } else {
        return [...prev, day].sort((a, b) => a - b)
      }
    })
    setTimeout(() => scrollToDay(day), 100)
  }

  useEffect(() => {
    if (dayIndex !== undefined && !selectedDays.includes(dayIndex)) {
      setSelectedDays([dayIndex])
      setTimeout(() => scrollToDay(dayIndex), 200)
    }
  }, [dayIndex])

  // 对比模式下退出 bar-box 模式
  useEffect(() => {
    if (compareRoas && chartMode === 'bar-box') {
      setChartMode('line')
    }
  }, [compareRoas, chartMode])

  const handleToggleAllDays = () => {
    const availableDays = TargetDays.filter((day) => day <= maxDay)
    if (selectedDays.length === availableDays.length) {
      setSelectedDays(availableDays.length > 0 ? [availableDays[0]] : [])
    } else {
      setSelectedDays([...availableDays])
    }
  }

  const { chartData, yMin, yMax, chartConfig, referenceLinesData, maxDay } =
    useChartData({
      roas,
      compareRoas,
      selectedDays,
      selectedModels,
      chartMode,
      displayMode,
      availableModels,
      day0RoasLine,
      day0RoasLineTotalFallback,
      yAxisMode,
    })

  const yTickMode =
    displayMode === 'percentage'
      ? 'percent'
      : displayMode === 'multiplier'
        ? 'multiplier'
        : 'auto'

  const yTicks = useMemo(
    () => buildNiceTicks(yMin, yMax, Y_TICK_COUNT, { mode: yTickMode }),
    [yMin, yMax, yTickMode],
  )

  const calculatedChartWidth = useMemo(() => {
    const baseWidthPerDataPoint = 12
    const minBarWidth = 8
    const numDataPoints = chartData.length
    let calculatedMinWidth = 0

    if (chartMode === 'bar-box') {
      const numBarsPerDataPoint = selectedDays.length * selectedModels.length
      calculatedMinWidth =
        numDataPoints *
        (baseWidthPerDataPoint +
          numBarsPerDataPoint * minBarWidth +
          (numBarsPerDataPoint > 0 ? numBarsPerDataPoint * 2 : 0))
    } else {
      calculatedMinWidth = numDataPoints * baseWidthPerDataPoint
    }
    const minChartDisplayWidth = Math.max(window.innerWidth * 0.5, 600)
    return Math.max(calculatedMinWidth, minChartDisplayWidth)
  }, [chartData.length, chartMode, selectedDays.length, selectedModels.length])

  if (!roas?.dailyData) return null
  const showPercentage = displayMode === 'percentage'
  const showMultiplier = displayMode === 'multiplier'
  const availableDays = TargetDays.filter((day) => day <= maxDay)

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>
            Day-{selectedDays.join(', ')}{' '}
            {showPercentage
              ? 'ROAS'
              : showMultiplier
              ? 'Multiplier'
              : 'Revenue'}
            {compareRoas &&
              (Array.isArray(compareRoas) ? compareRoas.length > 0 : true) && (
                <>
                  {` (Compare: ${(Array.isArray(compareRoas)
                    ? compareRoas
                    : [compareRoas]
                  )
                    .map((c) => shortCampaignLabel(c.campaignName || 'Compare'))
                    .join(', ')})`}
                  {compareDiffMode && (
                    <span className="ml-2 text-primary font-bold">(Diff)</span>
                  )}
                </>
              )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Tabs value={displayMode} onValueChange={setDisplayMode}>
              <TabsList className="h-9">
                <TabsTrigger value="amount">$</TabsTrigger>
                <TabsTrigger value="percentage">%</TabsTrigger>
                <TabsTrigger value="multiplier">x</TabsTrigger>
              </TabsList>
            </Tabs>
            {showMultiplier && (
              <Select
                value={multiplierDenominatorMode}
                onValueChange={setMultiplierDenominatorMode}
              >
                <SelectTrigger className="w-[140px] h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="d0_revenue">D0 Revenue</SelectItem>
                  <SelectItem value="target_roas" disabled={!hasTargetRoas}>
                    Target ROAS {!hasTargetRoas && '(No Target ROAS Set)'}
                  </SelectItem>
                  <SelectItem value="growth_rate">Growth Rate</SelectItem>
                </SelectContent>
              </Select>
            )}
            {!compareRoas && (
              <Tabs value={dayMode} onValueChange={setDayMode}>
                <TabsList className="h-9">
                  <TabsTrigger value="daily">Daily</TabsTrigger>
                  <TabsTrigger value="cumulative">Cumulative</TabsTrigger>
                </TabsList>
              </Tabs>
            )}
            <Select value={chartMode} onValueChange={setChartMode}>
              <SelectTrigger className="w-[120px] h-9">
                <SelectValue placeholder="Chart Mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="line">Line</SelectItem>
                {!compareRoas && (
                  <SelectItem value="bar-box">Bar-Box</SelectItem>
                )}
              </SelectContent>
            </Select>
            {compareRoas &&
              (Array.isArray(compareRoas) ? compareRoas.length > 0 : true) && (
                <Tabs
                  value={compareDiffMode ? 'diff' : 'normal'}
                  onValueChange={(v) => setCompareDiffMode(v === 'diff')}
                >
                  <TabsList className="h-9">
                    <TabsTrigger value="normal">Normal</TabsTrigger>
                    <TabsTrigger value="diff">Diff</TabsTrigger>
                  </TabsList>
                </Tabs>
              )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <span className="text-sm text-muted-foreground self-center">
            Days:
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={handleToggleAllDays}
            className="h-7 px-2 text-xs"
          >
            {selectedDays.length === availableDays.length
              ? 'Deselect All'
              : 'Select All'}
          </Button>
          {TargetDays.map((day) => (
            <Button
              key={day}
              variant={selectedDays.includes(day) ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleDaySelect(day)}
              disabled={day > maxDay}
              className="h-7 px-2 text-xs"
              style={
                selectedDays.includes(day)
                  ? {
                      backgroundColor: getDayColor(day),
                      borderColor: getDayColor(day),
                      color: 'white',
                    }
                  : {}
              }
            >
              Day{day}
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-3 mb-3">
          <Tabs value={chartWidthMode} onValueChange={setChartWidthMode}>
            <TabsList className="h-9">
              <TabsTrigger value="fit" title="All data in one screen width">
                X: Fit
              </TabsTrigger>
              <TabsTrigger
                value="scrollable"
                title="Horizontal scroll for detail"
              >
                X: Scroll
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <Tabs value={yAxisMode} onValueChange={setYAxisMode}>
            <TabsList className="h-9">
              <TabsTrigger
                value="adaptive"
                title="Y-axis fits data range (10% padding)"
              >
                Y: Adaptive
              </TabsTrigger>
              <TabsTrigger
                value="fixed"
                title="Y-axis includes 0 (20% padding)"
              >
                Y: Fixed
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <ScrollableChart
          config={chartConfig}
          width={calculatedChartWidth}
          height={600}
          scrollToEnd={chartWidthMode === 'scrollable'}
          fitViewport={chartWidthMode === 'fit'}
          chartMargin={CHART_MARGIN}
          yAxis={{
            min: yMin,
            max: yMax,
            tickCount: Y_TICK_COUNT,
            ticks: yTicks,
            formatter: (value) => {
              if (showPercentage) return `${value.toFixed(0)}%`
              if (showMultiplier) return formatMultiplier(value, 0, 1)
              return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                notation: 'compact',
                maximumFractionDigits: 1,
              }).format(value)
            },
            showPercentage: showPercentage,
          }}
          referenceLines={referenceLinesData}
        >
          <ComposedChart
            data={chartData}
            margin={CHART_MARGIN}
            barCategoryGap="10%"
            barGap={1}
          >
            <CartesianGrid vertical={false} className="stroke-muted" />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              className="fill-muted-foreground text-sm"
              tickFormatter={(value) => {
                const parts = value.split(' 周')
                return parts.length > 1 ? parts[0] : value
              }}
            />
            <YAxis
              type="number"
              tickLine={false}
              axisLine={false}
              domain={[yMin, yMax]}
              ticks={yTicks}
              allowDataOverflow
              width={0}
            />
            <ChartTooltip
              content={
                <CustomTooltip
                  selectedDays={selectedDays}
                  selectedModels={selectedModels}
                  chartMode={chartMode}
                  showPercentage={showPercentage}
                  availableModels={availableModels}
                  compareRoas={compareRoas}
                  compareDiffMode={compareDiffMode}
                />
              }
            />
            {renderChartVisuals({
              chartMode,
              selectedDays,
              selectedModels,
              chartData,
              showPercentage,
              availableModels,
              compareRoas,
              compareDiffMode,
              areaBaseValue: yAxisMode === 'adaptive' ? yMin : 0,
            })}
          </ComposedChart>
        </ScrollableChart>
      </CardContent>
      <CardFooter className="flex flex-col gap-3">
        <ScrollArea ref={scrollAreaRef} className="w-full h-12">
          <div className="w-max">
            <div className="inline-flex h-9 items-center justify-start rounded-lg bg-muted p-1 text-muted-foreground gap-1">
              {Array.from({ length: maxDay + 1 }, (_, i) => (
                <Button
                  key={i}
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDaySelect(i)}
                  className="h-7 px-2 text-xs whitespace-nowrap flex-shrink-0 data-[selected=true]:bg-primary data-[selected=true]:text-primary-foreground"
                  data-selected={selectedDays.includes(i)}
                  style={
                    selectedDays.includes(i)
                      ? {
                          backgroundColor: getDayColor(i),
                          color: 'white',
                        }
                      : {}
                  }
                >
                  Day{i}
                </Button>
              ))}
            </div>
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </CardFooter>
    </Card>
  )
}

export default Day0Chart
